import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as DropdownIcon } from "./../assets/svgs/dropdown-icon.svg";

const MENU_MIN_WIDTH = 170;
const SEARCHABLE_MENU_MIN_WIDTH = 240;
const MENU_MAX_HEIGHT = 320;
const SEARCH_AREA_HEIGHT = 52;
const VIEWPORT_PADDING = 8;
const MENU_GAP = 8;

const FilterDropdown = ({
  label,
  options = [],
  onSelect,
  searchable = false,
  searchPlaceholder = "Search options...",
  emptyMessage = "No options found",
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: MENU_MIN_WIDTH,
  });

  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!searchable || !normalizedQuery) {
      return options;
    }
    return options.filter((option) => {
      const optionLabel = String(option?.label || "").toLowerCase();
      const optionValue = String(option?.value || "").toLowerCase();

      return (
        optionLabel.includes(normalizedQuery) ||
        optionValue.includes(normalizedQuery)
      );
    });
  }, [options, searchQuery, searchable]);

  const closeDropdown = ({ restoreFocus = false } = {}) => {
    setOpen(false);
    setSearchQuery("");
    if (restoreFocus) {
      window.requestAnimationFrame(() => {
        triggerRef.current?.focus();
      });
    }
  };

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;

    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const availableViewportWidth = window.innerWidth - VIEWPORT_PADDING * 2;

    const minimumWidth = searchable ? SEARCHABLE_MENU_MIN_WIDTH : MENU_MIN_WIDTH;
    const menuWidth = Math.min(
      Math.max(triggerRect.width, minimumWidth),
      availableViewportWidth,
    );

    const maximumLeft = window.innerWidth - menuWidth - VIEWPORT_PADDING;

    const left = Math.min(
      Math.max(triggerRect.left, VIEWPORT_PADDING),
      Math.max(VIEWPORT_PADDING, maximumLeft),
    );
    const searchHeight = searchable ? SEARCH_AREA_HEIGHT : 0;

    const estimatedMenuHeight = Math.min(
      filteredOptions.length * 38 + 8 + searchHeight,
      MENU_MAX_HEIGHT,
    );

    const spaceBelow = window.innerHeight - triggerRect.bottom;

    const spaceAbove = triggerRect.top;

    let top = triggerRect.bottom + MENU_GAP;

    // Open above the trigger when there is not enough room below.
    if (spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow) {
      top = Math.max(
        VIEWPORT_PADDING,
        triggerRect.top - estimatedMenuHeight - MENU_GAP,
      );
    }

    setMenuPosition({
      top,
      left,
      width: menuWidth,
    });
  };

  const handleToggle = (event) => {
    event.stopPropagation();

    if (open) {
      closeDropdown();
      return;
    }
    setOpen(true);
  };

  const handleOptionClick = (event, option) => {
    event.stopPropagation();

    onSelect?.(option);
    closeDropdown({ restoreFocus: true });
  };

  useLayoutEffect(() => {
    if (!open) return;

    updateMenuPosition();
  }, [
    open,
    filteredOptions.length,
    searchable,
  ]);

  useEffect(() => {
    if (!open || !searchable) return;
    const focusTimer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      const clickedTrigger = dropdownRef.current?.contains(event.target);

      const clickedMenu = menuRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedMenu) {
        closeDropdown();
      }
    };

    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      closeDropdown({ restoreFocus: true });
    };

    const handleViewportChange = () => {
      updateMenuPosition();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    window.addEventListener("resize", handleViewportChange);

    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);

      document.removeEventListener("keydown", handleEscape);

      window.removeEventListener("resize", handleViewportChange);

      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open]);

  return (
    <div ref={dropdownRef} className="relative" data-filter-dropdown>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={handleToggle}
        className="
          inline-flex items-center gap-2 whitespace-nowrap
          font-helvena text-[14px] font-medium text-[#313131]
        "
      >
        <span>{label}</span>

        <DropdownIcon
          className={`
            mb-1 transition-transform duration-200
            ${open ? "rotate-180" : ""}
          `}
        />
      </button>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuRef}
            data-filter-dropdown
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            style={{
              position: "fixed",
              top: menuPosition.top,
              left: menuPosition.left,
              width: menuPosition.width,
              zIndex: 10000,
            }}
            className="
              overflow-hidden rounded-[10px]
              border border-[#E3E3E6] bg-white
              shadow-[0_10px_30px_rgba(0,0,0,0.12)]
            "
          >
            {searchable ? (
              <div className="border-b border-[#E8E8EA] bg-white p-2">
                <input
                  ref={searchInputRef}
                  type="search"
                  value={searchQuery}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  autoComplete="off"
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  onKeyDown={(event) => {
                    event.stopPropagation();
                    if (event.key !== "Escape") return;
                    event.preventDefault();
                    if (searchQuery) {
                      setSearchQuery("");
                      return;
                    }
                    closeDropdown({
                      restoreFocus: true,
                    });
                  }}
                  className="
                    h-[36px] w-full rounded-[7px]
                    border border-[#D7D7DB]
                    bg-white px-3
                    font-helvena text-[13px]
                    text-[#2F2F32]
                    outline-none
                    placeholder:text-[#9A9AA1]
                    focus:border-[#929298]
                  "
                />
              </div>
            ) : null}

            <div
              role="listbox"
              className="
                max-h-[268px] overflow-y-auto p-1
                [scrollbar-width:thin]
              "
            >
            {filteredOptions.map((option) => (
              <button
                key={`${option.value}-${option.label}`}
                type="button"
                role="option"
                onClick={(event) => handleOptionClick(event, option)}
                className="
                  flex min-h-[36px] w-full items-center
                  rounded-[8px] px-3 py-2 text-left
                  text-[13px] text-[#2F2F32]
                  transition-colors
                  hover:bg-[#F5F5F6]
                  focus:bg-[#F5F5F6]
                  focus:outline-none
                "
              >
                {option.label}
              </button>
            ))}
            {filteredOptions.length === 0 ? (
              <div
                role="status"
                className="
                  px-4 py-6 text-center
                  font-helvena text-[12px]
                  text-[#8A8A90]
                "
              >
                {emptyMessage}
              </div>
            ) : null}
          </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default FilterDropdown;
